import {FormEvent, useEffect, useRef, useState} from 'react';
import './ForgotPasswordFlow.css';
import './AuthenticationFields.css';
import {HolmesJrGuide} from './HolmesJrGuide';
import {DEMO_RECOVERY_TOKENS} from './accountRecoveryTokens';
import {SignupBotCheck} from './SignupBotCheck';

const RESEND_COOLDOWN_SECONDS = 30;

const DEMO_LINK_LABEL: Record<string, string> = {
  'demo-valid-token': 'A working link',
  'demo-expired-token': 'A link that has expired',
  'demo-used-token': 'A link that was already used',
};

type Props = {
  /** Provided when embedded in AccountRecoveryWalkthrough.tsx; navigates client-side instead of a real link. */
  onOpenDemoLink?: (token: string) => void;
  onHelp?: () => void;
  live?: boolean;
};

export function ForgotPasswordFlow({onOpenDemoLink, onHelp, live=false}: Props) {
  const [step, setStep] = useState<'request' | 'sent'>('request');
  const [email, setEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [siteKey,setSiteKey]=useState(''),[botToken,setBotToken]=useState(''),[reset,setReset]=useState(0),[pending,setPending]=useState(false),[error,setError]=useState('');
  const confirmationHeadingRef = useRef<HTMLHeadingElement>(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (step === 'sent') {
      const frame = requestAnimationFrame(() => confirmationHeadingRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    }
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((current) => Math.max(0, current - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(()=>{if(!live)return;let active=true;void fetch('/api/v1/signup/config',{credentials:'omit',headers:{Accept:'application/json'}}).then(async response=>{const body=await response.json();if(!response.ok||body.enabled!==true||typeof body.site_key!=='string')throw new Error();if(active)setSiteKey(body.site_key);}).catch(()=>{if(active)setError('Password recovery is temporarily unavailable.');});return()=>{active=false};},[live]);

  async function sendRequest() {
    if(!live){setStep('sent');setCooldown(RESEND_COOLDOWN_SECONDS);return;}
    if(!botToken||sendingRef.current)return;sendingRef.current=true;setPending(true);setError('');
    try{const response=await fetch('/api/v1/auth/recovery/request',{method:'POST',credentials:'omit',headers:{Accept:'application/json','Content-Type':'application/json'},body:JSON.stringify({email:email.trim(),bot_token:botToken})});const body=await response.json();if(!response.ok||body.accepted!==true)throw new Error();setStep('sent');setCooldown(RESEND_COOLDOWN_SECONDS);}
    catch{setError('We could not send a reset link. Please wait and try again.');}
    finally{sendingRef.current=false;setPending(false);setBotToken('');setReset(value=>value+1);}
  }

  function submitRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Deliberately no branching on whether an account actually exists for this email --
    // showing the same confirmation either way avoids letting this form be used to check
    // who has a ProFenz account. Real send/no-send logic and rate limiting are Codex's lane.
    void sendRequest();
  }

  function resend() {
    if (cooldown > 0) return;
    if(live){setStep('request');setCooldown(0);}else setCooldown(RESEND_COOLDOWN_SECONDS);
  }

  if (step === 'sent') {
    return (
      <section aria-label="Password reset link sent" className="fpw-root">
        <HolmesJrGuide pose="guide" tip="If you don't see the email in a minute or two, check spam before requesting a new link." />
        {!live&&<p className="fpw-flag" role="status">
          <strong>Local synthetic module</strong>
          <span>No email is actually sent. Nothing here is stored or billed.</span>
        </p>}
        <div className="fpw-confirmation">
          <p className="fpw-eyebrow">CHECK YOUR EMAIL</p>
          <h1 ref={confirmationHeadingRef} tabIndex={-1}>Check your email</h1>
          <p className="fpw-confirmation-message">
            If an account exists for <strong className="fpw-confirmation-address">{email || 'that address'}</strong>, we've sent a password reset link.
          </p>
          <p className="fpw-muted">The link is valid for a limited time and can only be used once, for your account's security.</p>
          <div className="fpw-actions-row">
            <button type="button" className="fpw-secondary" onClick={resend} disabled={cooldown > 0}>
              {cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend link'}
            </button>
            {onHelp ? (
              <button type="button" className="fpw-link" onClick={onHelp}>
                No longer have access to this email?
              </button>
            ) : (
              <a className="fpw-link" href="/web/walkthrough/account-access-help?context=password">
                No longer have access to this email?
              </a>
            )}
          </div>

          {!live&&<div className="fpw-demo-panel">
            <p className="fpw-demo-title">For this synthetic module — a real email isn't sent here</p>
            <p className="fpw-muted">Open one of these to see exactly what a customer would experience from each kind of link:</p>
            <ul className="fpw-demo-links">
              {Object.entries(DEMO_RECOVERY_TOKENS).map(([token]) => (
                <li key={token}>
                  {onOpenDemoLink ? (
                    <button type="button" onClick={() => onOpenDemoLink(token)}>
                      {DEMO_LINK_LABEL[token] ?? token} →
                    </button>
                  ) : (
                    <a href={`/web/walkthrough/reset-password?token=${token}`}>{DEMO_LINK_LABEL[token] ?? token} →</a>
                  )}
                </li>
              ))}
              <li>
                {onOpenDemoLink ? (
                  <button type="button" onClick={() => onOpenDemoLink('not-a-real-token')}>
                    A broken or tampered-with link →
                  </button>
                ) : (
                  <a href="/web/walkthrough/reset-password?token=not-a-real-token">A broken or tampered-with link →</a>
                )}
              </li>
            </ul>
          </div>}
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Forgot password" className="fpw-root">
      <HolmesJrGuide pose="guide" tip="Enter the email on your ProFenz account and we'll send a link to reset your password." />
      {!live&&<p className="fpw-flag" role="status">
        <strong>Local synthetic module</strong>
        <span>Every field below is held in this browser only. Nothing is sent, stored, or billed.</span>
      </p>}
      <header className="fpw-header">
        <p className="fpw-eyebrow">FORGOT PASSWORD</p>
        <h1>Reset your password</h1>
        <p>We'll email you a link to choose a new one.</p>
      </header>
      <form className="fpw-form" onSubmit={submitRequest}>
        {error&&<p className="notice error" role="alert">{error}</p>}
        <div className="fpw-field">
          <label htmlFor="fpw-email">Email</label>
          <input className="auth-light-input" id="fpw-email" type="email" autoComplete="username" required disabled={pending} value={email} onChange={(event) => { if (!sendingRef.current) setEmail(event.target.value); }} />
        </div>
        {live&&siteKey&&<SignupBotCheck siteKey={siteKey} action="account_recovery_request" reset={reset} onToken={setBotToken}/>}
        <div className="fpw-actions">
          <button type="submit" className="fpw-primary" disabled={pending||(live&&(!siteKey||!botToken))}>
            {pending?'Sending…':'Send reset link'}
          </button>
        </div>
      </form>
      <p className="fpw-footer-link">
        <a href="/sign-in">← Back to sign in</a>
      </p>
    </section>
  );
}
